import { API_BASE_URL, ITEMS_PER_PAGE } from "../constants/app";

type RequestOptions = RequestInit & { headers?: Record<string, string> };

class ApiService {
  private static async request(endpoint: string, options: RequestOptions = {}) {
    const url = `${API_BASE_URL}${endpoint}`;

    const defaultHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const user = JSON.parse(localStorage.getItem("inspirelens_user") || "null");
    if (user?.token) {
      defaultHeaders.Authorization = `Bearer ${user.token}`;
    }

    const mergedHeaders: Record<string, string> = {
      ...defaultHeaders,
      ...(options.headers || {}),
    };

    const config: RequestInit = {
      ...options,
      headers: mergedHeaders as HeadersInit,
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData.message || errorData.error || `HTTP error! status: ${response.status}`;
      if ((response.status === 401 || response.status === 403) && typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("auth:tokenexpired", {
            detail: { message },
          })
        );
      }
      throw new Error(message);
    }

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return response.json();
    }

    return response.text();
  }

  static get(endpoint: string, params = {}, options: RequestOptions = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url, { method: "GET", ...options });
  }

  static post(endpoint: string, data = {}) {
    return this.request(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  static put(endpoint: string, data = {}) {
    return this.request(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  static delete(endpoint: string, data?: object) {
    return this.request(endpoint, { method: "DELETE", ...(data ? { body: JSON.stringify(data) } : {}) });
  }

  static patch(endpoint: string, data = {}) {
    return this.request(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  static getAllPosts(params: { sort?: string; page?: number; limit?: number } = {}) {
    return this.get("/", params);
  }

  static getAllCategories() {
    return this.get("/categories");
  }

  static getUserProfile(userId: number | string) {
    return this.get(`/users/${userId}`);
  }

  static getUserPosts(userId: number | string) {
    return this.get(`/users/${userId}/posts`);
  }

  static getFollowingFeed(userId: number | string) {
    return this.get(`/users/${userId}/following-feed`);
  }

  static getFollowStatus(userId: number | string) {
    return this.get(`/users/${userId}/follow-status`);
  }

  static toggleFollow(userId: number | string) {
    return this.post(`/users/${userId}/follow`);
  }

  static getFollowers(userId: number | string, limit = 8, offset = 0) {
    return this.get(`/users/${userId}/followers`, { limit, offset });
  }

  static getFollowing(userId: number | string, limit = 8, offset = 0) {
    return this.get(`/users/${userId}/following`, { limit, offset });
  }

  static getLikedPosts(params: { type?: string; category?: string | number } = {}) {
    const query: Record<string, string | number> = {};
    if (params.type && params.type !== "all") query.type = params.type;
    if (params.category && params.category !== "all") query.category = params.category;
    return this.get("/liked", query);
  }

  // Notification centre: every type (follows, new posts, likes, comments,
  // replies, mentions), newest first, a page at a time.
  static getNotifications(params: { filter?: "all" | "unread" | "read"; offset?: number; limit?: number } = {}) {
    return this.get("/notifications", {
      filter: params.filter ?? "all",
      offset: params.offset ?? 0,
      limit: params.limit ?? 20,
    });
  }

  static getUnreadNotificationCount() {
    return this.get("/notifications/unread-count", { scope: "all" });
  }

  static markNotificationsRead() {
    return this.post("/notifications/read-all");
  }

  static markNotificationRead(id: number) {
    return this.patch(`/notifications/${id}/read`);
  }

  static deleteNotification(id: number) {
    return this.delete(`/notifications/${id}`);
  }

  static getNotificationPreferences() {
    return this.get("/notifications/preferences");
  }

  static updateNotificationPreferences(preferences: Record<string, boolean>) {
    return this.put("/notifications/preferences", preferences);
  }

  static getFriendRecommendations() {
    return this.get("/recommendations");
  }

  static getContentByCategory(category: string, page = 1, type = "all", sort: "trending" | "recent" = "trending") {
    const query: Record<string, string | number> = { page, limit: ITEMS_PER_PAGE };
    if (category !== "all") query.category = category;
    if (type !== "all") query.type = type;
    if (sort === "recent") query.sort = sort;
    return this.get("/content-type", query);
  }

  // Every post in one category (no paging) - used to build that category's tag cloud.
  static getAllContentInCategory(category: string | number) {
    return this.get("/content-type", { category: String(category) });
  }

  static getContentByType(type: string, page = 1, category = "all", sort: "trending" | "recent" = "trending") {
    const query: Record<string, string | number> = { page, limit: ITEMS_PER_PAGE };
    if (type !== "all") query.type = type;
    if (category !== "all") query.category = category;
    if (sort === "recent") query.sort = sort;
    return this.get("/content-type", query);
  }

  // Standalone AI Prompts, optionally filtered by complexity level
  // ("beginner" | "intermediate" | "advanced") - powers /ai-prompts.
  static getAiPrompts(complexity?: string) {
    const query: Record<string, string> = { type: "prompt" };
    if (complexity && complexity !== "all") query.complexity = complexity;
    return this.get("/content-type", query);
  }

  // --- AI Topics (separate feature: topics grouping related AI Prompts,
  // each with its own up/down votes) ---

  static getAiTopics() {
    return this.get("/ai-topics");
  }

  static createAiTopic(data: { title: string; description?: string; category: string }) {
    return this.post("/ai-topics", data);
  }

  static getAiTopic(topicId: number | string) {
    return this.get(`/ai-topics/${topicId}`);
  }

  static updateAiTopic(topicId: number | string, data: { title: string; description?: string; category: string }) {
    return this.put(`/ai-topics/${topicId}`, data);
  }

  static deleteAiTopic(topicId: number | string) {
    return this.delete(`/ai-topics/${topicId}`);
  }

  static addAiTopicPrompt(topicId: number | string, data: { content: string; complexity?: string }) {
    return this.post(`/ai-topics/${topicId}/prompts`, data);
  }

  static updateAiTopicPrompt(topicId: number | string, promptId: number | string, data: { content: string; complexity?: string }) {
    return this.put(`/ai-topics/${topicId}/prompts/${promptId}`, data);
  }

  static deleteAiTopicPrompt(topicId: number | string, promptId: number | string) {
    return this.delete(`/ai-topics/${topicId}/prompts/${promptId}`);
  }

  static voteAiTopicPrompt(topicId: number | string, promptId: number | string, voteType: "up" | "down") {
    return this.post(`/ai-topics/${topicId}/prompts/${promptId}/vote`, { voteType });
  }

  static getQuotesByAuthorSlug(slug: string) {
    return this.get(`/quotes/author/${encodeURIComponent(slug)}`);
  }

  // Every quote author with their quote count ({ authors: [{ name, slug, count }] }).
  static getQuoteAuthors() {
    return this.get("/quotes/authors");
  }

  static submitFeedback(data: {
    rating: number;
    feedbackType: string;
    message: string;
    recommend?: string;
    name?: string;
    email?: string;
  }) {
    return this.post("/feedback", data);
  }

  // Every tag with how many posts carry it ({ tags: [{ name, slug, count }] }).
  static getTagCloud() {
    return this.get("/tags/cloud");
  }

  static getPostsByTagSlug(slug: string) {
    return this.get(`/tags/${encodeURIComponent(slug)}`);
  }

  static login(credentials = {}) {
    return this.post("/auth/login", credentials);
  }

  static checkEmail(email = {}) {
    return this.get("/auth/check-email", email);
  }

  static signup(userData = {}) {
    return this.post("/auth/signup", userData);
  }

  static verifyOtp(data = {}) {
    return this.post("/auth/verify-otp", data);
  }

  static resendOtp(data = {}) {
    return this.post("/auth/resend-otp", data);
  }

  static googleAuth(payload: { tokenId: string }) {
    return this.post("/auth/google", payload);
  }

  static forgotPassword(email: string) {
    return this.post("/auth/forgot-password", { email });
  }

  static resetPassword(data = {}) {
    return this.post("/auth/reset-password", data);
  }

  static logout() {
    return this.post("/auth/logout");
  }

  static updateTheme(theme: string, userId: string) {
    return this.put("/auth/theme", { theme, userId });
  }

  static vote(contentId: number, contentType: string, voteType: "upvote" | "downvote", userId: number) {
    return this.put(`/content-type/${contentId}/vote`, {
      contentType,
      voteType,
      userId,
    });
  }

  static addComment(contentId: number, postType: string, comment: string, userId: number, parentId?: number | null) {
    return this.post(`/posts/${contentId}/comments`, {
      comment,
      postType,
      userId,
      ...(parentId ? { parentId } : {}),
    });
  }

  static deleteComment(contentId: number, commentId: number, userId: number) {
    return this.delete(`/posts/deleteComment/${contentId}/comments/${commentId}?userId=${userId}`);
  }

  static createContent(contentData = {}) {
    return this.post("/posts/addContent", contentData);
  }

  static updateContent(contentId: number, contentData = {}) {
    return this.put(`/posts/${contentId}/updateContent`, contentData);
  }

  static deleteContent(contentId: number, contentType: string) {
    return this.delete(`/posts/${contentId}/deleteContent?contentType=${contentType}`);
  }

  static addTag(contentId: number, postType: string, tag: string) {
    return this.post(`/posts/${contentId}/tags`, { postType, tag });
  }

  static removeTag(contentId: number, postType: string, tag: string) {
    return this.delete(`/posts/${contentId}/tags/${encodeURIComponent(tag)}?postType=${postType}`);
  }

  static getPostById(postType: string, postId: number) {
    return this.get(`/posts/${postType}/${postId}`);
  }

  static getDigestPreference(authToken?: string) {
    const headers: Record<string, string> = {};
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }
    return this.get('/auth/digest-preference', {}, { headers });
  }

  static updateDigestPreference(subscribe: boolean) {
    return this.put('/auth/digest-preference', { subscribe });
  }

  static deleteAccount() {
    return this.delete('/auth/account');
  }

  static registerPushDevice(data: { token: string; platform: "android" | "ios" | "web"; deviceId?: string }) {
    return this.post('/push/devices', data);
  }

  static unregisterPushDevice(token: string) {
    return this.delete('/push/devices', { token });
  }

  static reportContent(data: { postType: string; postId: number; commentId?: number; reason: string; details?: string }) {
    return this.post('/feedback/report', data);
  }

  static createShortLink(data: { targetUrl: string; postId?: number | string; postType?: string }) {
    console.log('Creating short link with data:', data);
    return this.post('/short-links', data);
  }

  static parseArticle(url: string): Promise<{ title: string; author: string | null; excerpt: string | null; content: string; url: string }> {
    return this.post('/articles/parse', { url });
  }

  static getOgPreview(url: string): Promise<{ success: boolean; data?: { title: string; description: string; image: string; url: string } }> {
    return this.get('/og-preview', { url });
  }

  // --- TheDeepam Listen (public) ---

  static getListenCategories() {
    return this.get('/listen/categories');
  }

  static getFeaturedEpisode() {
    return this.get('/listen/featured');
  }

  static getCategoryEpisodes(categorySlug: string, page = 1, limit = 12) {
    return this.get(`/listen/categories/${categorySlug}/episodes`, { page, limit });
  }

  static getEpisodeBySlug(slug: string) {
    return this.get(`/listen/episodes/${slug}`);
  }

  // --- TheDeepam Listen (admin) ---

  static validatePodcastFeed(feedUrl: string) {
    return this.post('/admin/listen/feeds/validate', { feedUrl });
  }

  static addPodcastFeed(data: {
    feedUrl: string;
    active?: boolean;
    autoImport?: boolean;
    requiresReview?: boolean;
    syncFrequencyMinutes?: number;
    defaultCategoryIds?: number[];
  }) {
    return this.post('/admin/listen/feeds', data);
  }

  static getAdminPodcastFeeds() {
    return this.get('/admin/listen/feeds');
  }

  static updatePodcastFeed(feedId: number, data: Record<string, unknown>) {
    return this.request(`/admin/listen/feeds/${feedId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  static removePodcastFeed(feedId: number) {
    return this.request(`/admin/listen/feeds/${feedId}`, { method: 'DELETE', body: JSON.stringify({ confirm: true }) });
  }

  static syncPodcastFeedNow(feedId: number) {
    return this.post(`/admin/listen/feeds/${feedId}/sync`);
  }

  static getAdminPodcastEpisodes(params: { status?: string; feedId?: number; page?: number; limit?: number } = {}) {
    return this.get('/admin/listen/episodes', params as Record<string, any>);
  }

  static updatePodcastEpisode(episodeId: number, data: { displayTitle?: string; editorialSummary?: string; categoryIds?: number[] }) {
    return this.request(`/admin/listen/episodes/${episodeId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  static publishPodcastEpisode(episodeId: number) {
    return this.post(`/admin/listen/episodes/${episodeId}/publish`);
  }

  static hidePodcastEpisode(episodeId: number) {
    return this.post(`/admin/listen/episodes/${episodeId}/hide`);
  }

  static rejectPodcastEpisode(episodeId: number) {
    return this.post(`/admin/listen/episodes/${episodeId}/reject`);
  }

  static featurePodcastEpisode(episodeId: number, featured = true) {
    return this.post(`/admin/listen/episodes/${episodeId}/feature`, { featured });
  }
}

export default ApiService;
